import { Injectable, EventEmitter } from '@angular/core';
import { ApiService } from './api.service';

export interface ParkingSpaceType {
    Id: string;
    Name: string;
    Color: string;
    MediaId?: string;
}

@Injectable({
    providedIn: 'root'
})
export class ParkingSpaceTypesService {
    private spaceTypes: Map<string, ParkingSpaceType> = new Map();
    private iconCache: Map<string, string> = new Map();
    private loadedImages: Map<string, HTMLImageElement> = new Map();
    private loadingPromise: Promise<void> | null = null;
    public onSpaceTypesLoaded = new EventEmitter<void>();

    constructor(private apiService: ApiService) {}

    ensureLoaded(): Promise<void> {
        if (this.loadingPromise) {
            return this.loadingPromise;
        }

        this.loadingPromise = this.loadSpaceTypes();
        return this.loadingPromise;
    }

    private loadSpaceTypes(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.apiService.Get<ParkingSpaceType[]>('/infrastructure/spacetypes')
                .then(types => {
                    // Clear existing cache
                    this.spaceTypes.clear();
                    
                    // Cache all space types
                    for (const type of types) {
                        this.spaceTypes.set(type.Id.toString(), type);
                        
                        // If the type has a media ID, load its icon
                        if (type.MediaId) {
                            this.loadIcon(type.MediaId);
                        }
                    }

                    // Emit the loaded event
                    this.onSpaceTypesLoaded.emit();
                    resolve();
                })
                .catch(error => {
                    console.error('Failed to load parking space types:', error);
                    reject(error);
                });
        });
    }

    private loadIcon(mediaId: string): void {
        if (this.iconCache.has(mediaId)) {
            return;
        }

        this.apiService.Get<any>(`/media/${mediaId}/base64`)
            .then(response  => {
                // Create an image and set up loading
                const img = new Image();
                
                // Combine prefix and base64 data to create complete data URL
                const dataUrl = response.Base64Prefix + response.Base64String;
                
                // Set the image source to the data URL
                img.src = dataUrl;
                // Set up onload before setting src
                img.onload = () => {
                    // Once loaded, store in caches
                    this.iconCache.set(mediaId, "");
                    this.loadedImages.set(mediaId, img);
                    // Request a redraw since we have new content
                    this.onSpaceTypesLoaded.emit();
                };
            })
            .catch(error => {
                console.error(`Failed to load icon for media ID ${mediaId}:`, error);
            });
    }

    getSpaceType(typeId: string): ParkingSpaceType | undefined {
        return this.spaceTypes.get(typeId.toString());
    }

    getIcon(mediaId: string): string | undefined {
        return this.iconCache.get(mediaId.toString());
    }

    getLoadedImage(mediaId: string): HTMLImageElement | undefined {
        return this.loadedImages.get(mediaId);
    }
} 
